import { inject, injectable } from 'src/features/ioc';
import { setAccessToken as setLocatesClientAccessToken } from 'src/lib/locates/modern';
import { pushServiceSocket, sessionBarrierSocket } from 'src/lib/push';
import { getAccessTokens, getClientCredentials, getCurrentIsSuperuserEnabledFlag } from 'src/store/selectors';
import type { IsomorphicBindings } from 'src/types/bindings';

/**
 * Syncs various data from the store to external services
 */
@injectable()
export class SyncInternalConfigurationsServiceImpl {
    #disposableStack = new DisposableStack();

    constructor(
        @inject('Store') private readonly store: IsomorphicBindings['Store'],
        @inject('ProxyClientService') private readonly proxyClientService: IsomorphicBindings['ProxyClientService'],
    ) {
        this.#disposableStack.defer(
            this.store
                .subscribeSelector(getAccessTokens, { leading: true })
                .handle((tokens) => this.syncAccessTokens(tokens)),
        );
        this.#disposableStack.defer(
            this.store
                .subscribeSelector(getClientCredentials, { leading: true })
                .handle((creds) => this.syncClientCredentials(creds)),
        );
        this.#disposableStack.defer(
            this.store
                .subscribeSelector(getCurrentIsSuperuserEnabledFlag, { leading: true })
                .handle((flag) => this.syncIsSuperuserEnabledFlag(flag)),
        );
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    private syncClientCredentials(clientCredentials: ReturnType<typeof getClientCredentials>) {
        // Auth all the internal services to the new token
        this.proxyClientService.setAccessToken(clientCredentials);
    }

    private syncAccessTokens(tokens: ReturnType<typeof getAccessTokens>) {
        // Get the effective access token (masq takes precedence over regular)
        const effectiveAccessToken = tokens.masqAccessToken?.token ?? tokens.accessToken?.token;

        if (!effectiveAccessToken) return;

        // Auth all the internal services to the effective token
        pushServiceSocket.setAccessToken(effectiveAccessToken);
        this.proxyClientService.setAccessToken(effectiveAccessToken);
        setLocatesClientAccessToken(effectiveAccessToken);

        // Session barrier socket only gets the regular access token (no masquerading)
        if (tokens.accessToken?.token) {
            sessionBarrierSocket.setAccessToken(tokens.accessToken.token);
        }
    }

    private syncIsSuperuserEnabledFlag(flag: ReturnType<typeof getCurrentIsSuperuserEnabledFlag>) {
        this.proxyClientService.setSuperuserFlag(flag);
    }
}
